// Okabe-Ito colorblind-safe palette and per-species color families.
import type { Species } from "@eb/shared";

export const OKABE_ITO = {
  black: 0x000000,
  orange: 0xe69f00,
  skyBlue: 0x56b4e9,
  bluishGreen: 0x009e73,
  yellow: 0xf0e442,
  blue: 0x0072b2,
  vermillion: 0xd55e00,
  reddishPurple: 0xcc79a7,
} as const;

/** Outline / ink color used for bold outlines everywhere. */
export const INK = 0x1e1a2e;

export const SPECIES_LIST: Species[] = ["thumper", "snapper", "chirper", "clapper", "wobbler", "glimmer", "critter"];
/** Synth instrument characters. */
export const SYNTH_SPECIES: Species[] = ["woodwump", "acidmite", "choruspede", "operatoad", "drawbear", "boomer"];
export const ALL_SPECIES: Species[] = [...SPECIES_LIST, ...SYNTH_SPECIES];

/**
 * Color family per species: index 0 is the canonical Okabe-Ito hue, the rest are tints/shades
 * of the same hue so species stay recognisable for colorblind users (silhouettes differ too).
 */
export const SPECIES_COLORS: Record<Species, number[]> = {
  thumper: [0xe69f00, 0xf2b43a, 0xc98a00, 0xf7c96b],
  snapper: [0xd55e00, 0xe8752a, 0xb84f00, 0xf0956a],
  chirper: [0xf0e442, 0xf5ec7a, 0xd8cc2a, 0xfff3a8],
  clapper: [0x56b4e9, 0x7cc6ef, 0x3d9fd6, 0xa6daf6],
  wobbler: [0xcc79a7, 0xdb98bd, 0xb8628f, 0xe9bad4],
  glimmer: [0x009e73, 0x2bb88f, 0x00865f, 0x6fd3b4],
  critter: [0x0072b2, 0x2a8fcb, 0x005c90, 0x67afdc],
  // Synth characters: instrument materials, accented with Okabe-Ito hues.
  woodwump: [0x8a5a2b, 0xa66f38, 0x6e4520, 0xe69f00], // walnut cheeks, orange knobs
  acidmite: [0xb9bec7, 0xd2d6dc, 0x8e949e, 0x56b4e9], // brushed silver, sky-blue accents
  choruspede: [0x4a4f5c, 0x60667a, 0x353945, 0xd55e00], // charcoal body, vermillion stripes
  operatoad: [0x7a5a3f, 0x93714f, 0x5e4430, 0x009e73], // brown slab, green membrane buttons
  drawbear: [0x9b4a2c, 0xb5603e, 0x7a3820, 0xf0e442], // red-brown tonewheel wood, yellow drawbars
  boomer: [0x3b3a3f, 0x55535a, 0x2a292d, 0xd55e00], // charcoal drum-machine body, step keys in vermillion/orange/yellow/cream
};

export const SPECIES_LABEL: Record<Species, string> = {
  thumper: "Thumper",
  snapper: "Snapper",
  chirper: "Chirper",
  clapper: "Clapper",
  wobbler: "Wobbler",
  glimmer: "Glimmer",
  critter: "Critter",
  woodwump: "Woodwump",
  acidmite: "Acidmite",
  choruspede: "Choruspede",
  operatoad: "Operatoad",
  drawbear: "Drawbear",
  boomer: "Boomer",
};

export function hex(c: number): string {
  return "#" + c.toString(16).padStart(6, "0");
}

/** Mix two 0xRRGGBB colors, t in 0..1. */
export function mix(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255,
    ag = (a >> 8) & 255,
    ab = a & 255;
  const br = (b >> 16) & 255,
    bg = (b >> 8) & 255,
    bb = b & 255;
  const r = Math.round(ar + (br - ar) * t),
    g = Math.round(ag + (bg - ag) * t),
    bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/** Rotate hue of a 0xRRGGBB color by `deg` degrees. */
export function hueShift(c: number, deg: number): number {
  if (deg === 0) return c;
  let r = ((c >> 16) & 255) / 255,
    g = ((c >> 8) & 255) / 255,
    b = (c & 255) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  h = (h * 60 + deg + 360) % 360;
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - C / 2;
  [r, g, b] =
    h < 60
      ? [C, X, 0]
      : h < 120
        ? [X, C, 0]
        : h < 180
          ? [0, C, X]
          : h < 240
            ? [0, X, C]
            : h < 300
              ? [X, 0, C]
              : [C, 0, X];
  return (Math.round((r + m) * 255) << 16) | (Math.round((g + m) * 255) << 8) | Math.round((b + m) * 255);
}
